import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertCircle,
  DollarSign,
  History,
  Package,
  TrendingUp,
} from 'lucide-react';
import type { StockHistoryItem } from '@/types/product';
import type { ProductStockData } from '@/services/branchs';

interface OverviewTabProps {
  stockData: ProductStockData | null | undefined;
  formatDate: (dateStr: string) => string;
  getActionIcon: (action: string) => React.ReactNode;
  isLoading?: boolean;
}
const mockStockHistory: StockHistoryItem[] = [
  {
    id: 1,
    date: '2024-10-25',
    type: 'adjustment',
    action: 'add',
    quantity: 10,
    reason: 'New shipment received',
    user: 'John Doe aa',
    previousStock: 5,
    newStock: 15,
  },
  {
    id: 2,
    date: '2024-10-24',
    type: 'sale',
    action: 'remove',
    quantity: 3,
    reason: 'Customer purchase',
    user: 'System',
    previousStock: 8,
    newStock: 5,
  },
  {
    id: 3,
    date: '2024-10-20',
    type: 'price_update',
    action: 'update',
    quantity: 0,
    previousPrice: 1199.99,
    newPrice: 1299.99,
    reason: 'Price adjustment',
    user: 'Admin',
    previousStock: 8,
    newStock: 8,
  },
  {
    id: 4,
    date: '2024-10-18',
    type: 'adjustment',
    action: 'remove',
    quantity: 2,
    reason: 'Damaged items',
    user: 'John Doe',
    previousStock: 10,
    newStock: 8,
  },
];

const StatCardSkeleton = () => (
  <Card>
    <CardHeader className="pb-3">
      <div className="flex items-center">
        <Skeleton className="h-4 w-4 mr-2 rounded-full" />
        <Skeleton className="h-4 w-24" />
      </div>
    </CardHeader>
    <CardContent>
      <Skeleton className="h-8 w-24 mb-1" />
      <Skeleton className="h-3 w-16" />
    </CardContent>
  </Card>
);

export function OverviewTab({
  stockData,
  formatDate,
  getActionIcon,
  isLoading = false,
}: OverviewTabProps) {
  return (
    <div className="space-y-3 md:space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
        {isLoading ? (
          <>
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
          </>
        ) : (
          <>
            <Card className="py-1 gap-0">
              <CardHeader className="pt-2">
                <CardTitle className="text-sm flex items-center">
                  <DollarSign className="h-4 w-4 mr-2 text-green-600" />
                  <span className="truncate">Current Price</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="py-2 !pb-2">
                <div className="text-2xl font-semibold">
                  ${stockData?.price || '0.00'}
                </div>
                <p className="text-xs text-muted-foreground mt-1">Per unit</p>
              </CardContent>
            </Card>

            <Card className="py-1 gap-0">
              <CardHeader className="pt-2">
                <CardTitle className="text-sm flex items-center">
                  <Package className="h-4 w-4 mr-2 text-blue-600" />
                  <span className="truncate">Current Stock</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-1 pb-2">
                <div className="text-2xl font-semibold">
                  {stockData?.quantity || '0'}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Units available
                </p>
              </CardContent>
            </Card>

            <Card className="py-1 gap-0">
              <CardHeader className="pt-2">
                <CardTitle className="text-sm flex items-center">
                  <TrendingUp className="h-4 w-4 mr-2 text-purple-600" />
                  <span className="truncate">Total Value</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-1 pb-2">
                <div className="text-2xl font-semibold">
                  $
                  {(
                    Number(stockData?.price || 0) *
                    Number(stockData?.quantity || 0)
                  ).toFixed(2)}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Inventory value
                </p>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {stockData?.quantity !== undefined &&
        stockData?.quantity <= 5 &&
        stockData?.quantity > 0 && (
          <Card className="border-yellow-200 bg-yellow-50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-yellow-900">
                    Low Stock Alert
                  </h4>
                  <p className="text-sm text-yellow-700 mt-1">
                    Stock is running low. Consider reordering soon to avoid
                    stockouts.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

      {stockData?.quantity !== undefined && stockData?.quantity === 0 && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-red-900">Out of Stock</h4>
                <p className="text-sm text-red-700 mt-1">
                  This product is currently out of stock. Restock immediately to
                  resume sales.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Separator />

      <div>
        <h3 className="font-medium mb-4 flex items-center">
          <History className="h-4 w-4 mr-2" />
          Recent Activity
        </h3>
        <div className="border rounded-lg">
          {isLoading ? (
            // Skeleton for the table when loading
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <div
                  key={i}
                  className="flex justify-between items-center p-4 border rounded-md"
                >
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-24" />
                  </div>
                  <Skeleton className="h-4 w-20" />
                </div>
              ))}
            </div>
          ) : (
            // Actual table when data is loaded
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead className="text-right">Quantity</TableHead>
                  <TableHead className="text-right">Stock</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {mockStockHistory.slice(0, 5).map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell className="text-sm">
                      {formatDate(entry.date)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center">
                        {getActionIcon(entry.action)}
                        <span className="ml-2">
                          {entry.action === 'add' ? 'Added' : 'Removed'} stock
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{entry.user}</TableCell>
                    <TableCell className="text-right font-medium">
                      {entry.quantity} units
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="font-medium">{entry.newStock}</span>
                      <span className="text-muted-foreground text-xs ml-1">
                        (was {entry.previousStock})
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </div>
  );
}
